// src/Toast.style.tsx
import {
  css,
  keyframes
} from "@emotion/react";
import { bodyB, subTitle3 } from "@teamsparta/stack-font";
import { applyOpacityToHexColor } from "@teamsparta/stack-utils";
import { isNumber } from "@teamsparta/utils";
import { Fragment } from "react";
import { jsx } from "@emotion/react/jsx-runtime";
var EXIT_DURATION = 300;
var ENTER_DURATION = 300;
var DEFAULT_OFFSET = 16;
var DEFAULT_BACKGROUND_COLOR = "#5F666B";
var toastSizeVariants = {
  md: {
    padding: "10px 14px"
  },
  lg: {
    padding: "12px 16px"
  }
};
var toastMessageWrapperVariants = {
  md: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0 2px"
  },
  lg: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0 4px"
  }
};
var toastMessageFontVariants = {
  md: bodyB,
  lg: subTitle3
};
var positionX = {
  center: () => css`
    left: 50%;
    transform: translateX(-50%);
  `
};
var positionY = {
  top: (offset) => css`
    top: ${offset};
  `,
  bottom: (offset) => css`
    bottom: ${offset};
  `
};
var toastBaseCss = (type) => type !== "custom" ? css({
  position: "fixed",
  borderRadius: "100px",
  backgroundColor: applyOpacityToHexColor(DEFAULT_BACKGROUND_COLOR, 0.8),
  backdropFilter: "blur(15px)",
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  zIndex: Number.MAX_SAFE_INTEGER
}) : css({
  position: "fixed",
  zIndex: Number.MAX_SAFE_INTEGER
});
var toastAddonCss = css({
  padding: "0 8px",
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  marginLeft: "auto"
});
var toastSizeCss = (sizes) => sizes.map(
  ({ breakpoint, size }) => css`
      @media (min-width: ${breakpoint}px) {
        ${toastSizeVariants[size]}
      }
    `
);
var toastStretchCss = (stretchs) => stretchs.map(
  ({ breakpoint, stretch }) => css`
      @media (min-width: ${breakpoint}px) {
        width: ${stretch ? "calc(100vw - 32px)" : "auto"};
        max-width: ${stretch ? "500px" : "none"};
      }
    `
);
var toastMessageWrapperCss = (sizes) => sizes.map(
  ({ breakpoint, size }) => css`
      @media (min-width: ${breakpoint}px) {
        ${toastMessageWrapperVariants[size]}
      }
    `
);
var toastMessageFontCss = (sizes) => sizes.map(
  ({ breakpoint, size }) => css`
      @media (min-width: ${breakpoint}px) {
        ${toastMessageFontVariants[size]}
      }
    `
);
var swipeInAnimation = (positionX2, positionY2, offset) => keyframes`
  from {
    opacity: 0;
    transform: translateX(${positionX2 === "center" ? "-50%" : 0}) translateY(${positionY2 === "top" ? "-" : ""}${offset});
  }

  to {
    opacity: 1;
    transform: translateX(${positionX2 === "center" ? "-50%" : 0}) translateY(0);
  }
`;
var fadeInAnimation = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;
var swipeOutAnimation = (positionX2, positionY2, offset) => keyframes`
  from {
    opacity: 1;
    transform: translateX(${positionX2 === "center" ? "-50%" : 0}) translateY(0);
  }

  to {
    opacity: 0;
    transform: translateX(${positionX2 === "center" ? "-50%" : 0}) translateY(${positionY2 === "top" ? "-" : ""}${offset});
  }
`;
var fadeOutAnimation = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;
var toastPositionCss = ({
  positions,
  offsets,
  dismiss,
  immediateDismiss,
  immediateEnter
}) => positions.map(({ breakpoint, position }) => {
  var _a, _b;
  const [y, x] = position.split("-");
  const offset = offsets.length === 1 ? offsets[0].offset : (_b = (_a = offsets.find(({ breakpoint: b }) => b === breakpoint)) == null ? void 0 : _a.offset) != null ? _b : DEFAULT_OFFSET;
  const calculatedOffset = isNumber(offset) ? `${offset}px` : offset;
  const enterAnimation = immediateEnter ? fadeInAnimation : swipeInAnimation(x, y, calculatedOffset);
  const exitAnimation = immediateDismiss ? fadeOutAnimation : swipeOutAnimation(x, y, calculatedOffset);
  return css`
      @media (min-width: ${breakpoint}px) {
        top: auto;
        bottom: auto;
        left: auto;
        right: auto;
        transform: none;
        animation: ${enterAnimation} ${ENTER_DURATION}ms forwards;

        ${dismiss && css`
          animation: ${exitAnimation} ${EXIT_DURATION}ms forwards;
        `};

        ${positionX[x](calculatedOffset)};
        ${positionY[y](calculatedOffset)}
      }
    `;
});
function byBreakpoints(nodes) {
  return /* @__PURE__ */ jsx(Fragment, { children: nodes.map(({ breakpoint, node }, index) => {
    var _a;
    const nextBreakpoint = (_a = nodes[index + 1]) == null ? void 0 : _a.breakpoint;
    return /* @__PURE__ */ jsx(
      "span",
      {
        css: css`
              display: none;

              @media (min-width: ${breakpoint}px) {
                display: contents;
              }

              ${nextBreakpoint && `@media (min-width: ${nextBreakpoint}px) {
                    display: none;
                  }`}
            `,
        children: node
      },
      breakpoint
    );
  }) });
}

export {
  EXIT_DURATION,
  toastBaseCss,
  toastAddonCss,
  toastSizeCss,
  toastStretchCss,
  toastMessageWrapperCss,
  toastMessageFontCss,
  toastPositionCss,
  byBreakpoints
};
